import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { alphaMonthYear } from "LIB/formatters/date";
import { dollars } from "LIB/formatters/currency";
import {
  EXCHANGE_FEE,
  LOOKING_FOR,
  NO_INTEREST,
  OUTGOING_LINE_ITEMS,
  PRICE_PER_UNIT,
  QUOTE_TYPE
} from "MODULES/OutgoingQuotes/constants";
import LineItemStatusCell from "MODULES/shared/components/LineItemStatusCell";
import { PartNumber, IconText, LocationFlag } from "MODULES/shared/components";
import ModifiedFlag, {
  columnStyle
} from "MODULES/parts/components/ModifiedFlag";
import OutgoingQuoteNote from "MODULES/OutgoingQuotes/shared/OutgoingQuoteNote";
import {
  CONDITION,
  INVENTORY,
  MODIFIED,
  SERIAL_NUMBER,
  STORE_LOCATION_ID,
  TAG_DATE,
  TAGGED_BY,
  TRACE_TYPE,
  TRACED_TO
} from "MODULES/inventories/constants";
import {
  CONDITIONS_WITH_SERVICEABLE,
  ID,
  INVENTORY_ID,
  QUANTITY,
  SUBTOTAL,
  TOTAL,
  UUID
} from "APP_ROOT/constants";
import { LookingForFlags } from "MODULES/OutgoingQuotes/shared/LookingForFlags";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import { EXCHANGE_ONLY } from "MODULES/IncomingQuotes/constants";
import Create from "./Create/Create";
import Edit from "../../shared/Parts/EditPart";
import Controls from "./Controls";

const InventoryLink = ({ lineItem }) => {
  const foundInventoryUUID = lineItem.getIn([INVENTORY, UUID]);
  const foundInventoryId = lineItem.get(INVENTORY_ID);
  const serial =
    lineItem.getIn([INVENTORY, SERIAL_NUMBER]) || lineItem.get(SERIAL_NUMBER);

  if (foundInventoryUUID || foundInventoryId) {
    return (
      <td>
        <Link
          onClick={e => e.stopPropagation()}
          to={`/inventories/${foundInventoryUUID || foundInventoryId}`}
        >
          {serial}
        </Link>
      </td>
    );
  }

  return <td>{serial}</td>;
};

export default class LineItemsTable extends Component {
  static propTypes = {
    quote: PropTypes.shape({
      outgoing_quote_line_items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          quote_type: PropTypes.string.isRequired,
          dom: PropTypes.string,
          condition: PropTypes.string,
          cost: PropTypes.string
        })
      )
    }).isRequired
  };

  state = {
    editModalOpen: false,
    selectedLineItem: null
  };

  openModal = lineItem =>
    this.setState({ editModalOpen: true, selectedLineItem: lineItem });

  closeModal = () => this.setState({ editModalOpen: false });

  renderRow = (lineItem, i) => (
    <tr
      key={lineItem.get(ID)}
      className="clickable"
      onClick={() => this.openModal(lineItem)}
    >
      <td>{i + 1}</td>
      <LineItemStatusCell lineItem={lineItem.toJS()}>
        <PartNumber part={lineItem.toJS().part} showClipBoard />
      </LineItemStatusCell>
      <InventoryLink lineItem={lineItem} />
      <td>
        {lineItem.get("sell_as_alt") &&
          (lineItem.getIn(["sell_as_alt", "alternate_part_id"]) ? (
            <PartNumber
              part={{
                id: lineItem.getIn(["sell_as_alt", "alternate_part_id"]),
                part_number: lineItem.getIn(["sell_as_alt", "name"])
              }}
            />
          ) : (
            lineItem.getIn(["sell_as_alt", "name"])
          ))}
      </td>
      <MultiLocationFeature>
        <td className="text-center">
          <LocationFlag
            location={lineItem.getIn([INVENTORY, STORE_LOCATION_ID])}
          />
        </td>
      </MultiLocationFeature>
      <td>{OUTGOING_LINE_ITEMS.quoteType[lineItem.get(QUOTE_TYPE)].label}</td>
      <td>
        {lineItem.get(TRACE_TYPE) &&
          OUTGOING_LINE_ITEMS.traceTypes[lineItem.get(TRACE_TYPE)].label}
      </td>
      <td>{lineItem.get(TRACED_TO)}</td>
      <td>{alphaMonthYear(lineItem.get(TAG_DATE))}</td>
      <td>{lineItem.get(TAGGED_BY)}</td>
      <td style={columnStyle.flagColumn}>
        {lineItem.getIn([INVENTORY, MODIFIED]) && <ModifiedFlag />}
      </td>
      <td style={columnStyle.conditionColumn}>
        {lineItem.get(CONDITION) &&
          CONDITIONS_WITH_SERVICEABLE[lineItem.get(CONDITION)].label}
      </td>
      <td>{lineItem.get(QUANTITY)}</td>
      <td className="text-right padding-right-zero">
        <LookingForFlags lineItem={lineItem.toJS()} />

        {lineItem.get(LOOKING_FOR) &&
          lineItem.get(NO_INTEREST) && <span>&nbsp;</span>}
        {lineItem.get(NO_INTEREST) && <IconText text="NI" note={NO_INTEREST} />}
      </td>
      <td className="text-center padding-left-zero">
        {lineItem.get(QUOTE_TYPE) === EXCHANGE_ONLY
          ? dollars(lineItem.get(EXCHANGE_FEE))
          : dollars(lineItem.get(PRICE_PER_UNIT))}
      </td>
      <td className="text-right">{dollars(lineItem.get(SUBTOTAL))}</td>
      <OutgoingQuoteNote lineItem={lineItem.toJS()} />
      <Controls lineItem={lineItem.toJS()} />
    </tr>
  );

  render() {
    const { quote } = this.props;
    const { editModalOpen, selectedLineItem } = this.state;

    return (
      <div>
        <h2 style={{ marginTop: 26 }}>
          Parts
          <Edit
            id={quote.get(ID)}
            open={editModalOpen}
            close={this.closeModal}
            quote={quote}
            selectedLineItem={selectedLineItem}
          />
          <Create quote={quote} />
        </h2>
        <table className="table table-striped table-hover table-numbered">
          <thead>
            <tr>
              <th />
              <th>Part #</th>
              <th>Serial Number</th>
              <th>Quote as Alt</th>
              <MultiLocationFeature>
                <th className="text-center">Store Loc</th>
              </MultiLocationFeature>
              <th>Quote Type</th>
              <th>Trace Type</th>
              <th>Trace To</th>
              <th>Tag Date</th>
              <th>Tagged By</th>
              <th />
              <th>Condition</th>
              <th>QTY</th>
              <th className="text-right" />
              <th className="text-right">Price Per Unit</th>
              <th className="text-right">Subtotal</th>
              <th />
              <th className="text-right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {quote.get("outgoing_quote_line_items", []).map(this.renderRow)}
            <tr style={{ backgroundColor: "transparent" }}>
              <td />
              <td className="text-right" colSpan={17}>
                <h4 className="margin-top-none margin-bottom-none">
                  Total: {dollars(quote.get(TOTAL))}
                </h4>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/modules/OutgoingQuotes/Detail/LineItems/LineItemsTable.jsx